import { Fade, Grid, IconButton, Paper, makeStyles } from "@material-ui/core";
import * as React from "react";
import { IconRemove } from "src/common/icons";

const useStyles = makeStyles((theme: any) => ({
  closeIcon: {
    fontSize: "1.4rem",
    lineHeight: "1.4rem"
  },
  container: {
    marginBottom: 12,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.2rem"
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "1.4rem"
    }
  },
  paper: {
    "&:before": {
      content: '""',
      height: "100%",
      left: 0,
      position: "absolute" as "absolute",
      top: 0,
      width: 3
    },
    padding: "6px 4px 6px 21px",
    position: "relative" as "relative"
  },
  textContainer: {
    display: "block" as "block",
    lineHeight: "4.4rem"
  },
  textContent: {
    color: "#4d4d4e",
    display: "inline-block" as "inline-block",
    lineHeight: "2.2rem",
    position: "relative" as "relative",
    top: "-2px",
    verticalAlign: "middle" as "middle"
  },
  typeInfo: {
    "&:before": {
      background: theme.palette.secondary.main
    }
  },
  typeWarning: {
    "&:before": {
      background: "#f44336"
    }
  }
}));

export interface IOwnProps {
  onClose?: () => void;
  render?: () => React.ReactNode;
  children?: React.ReactNode;
  duration?: number;
  scrollTo?: boolean;
  type?: "info" | "warning";
  className?: string;
}

const NotificationMessage = ({
  render,
  children,
  onClose,
  duration = 200,
  scrollTo = false,
  type = "info",
  className
}: IOwnProps) => {
  const containerRef = React.createRef<HTMLDivElement>();
  let scrollTimeout: NodeJS.Timer;
  const classes = useStyles();
  React.useEffect(() => {
    if (scrollTo) {
      clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(() => {
        if (containerRef.current) {
          const bodyRect = document.body.getBoundingClientRect();
          const elemRect = containerRef.current.getBoundingClientRect();
          const offset = elemRect.top - bodyRect.top;

          window.scrollTo({
            behavior: "smooth",
            top: offset - 90 // Header offset
          });
        }
      }, 100);
    }
    return () => {
      clearTimeout(scrollTimeout);
    };
  }, []);

  const [fadeing, setFade] = React.useState(true);

  const handleClose = React.useCallback(() => {
    setFade(false);
    setTimeout(() => onClose && onClose(), duration);
  }, [onClose, duration]);

  const timeout = {
    exit: duration
  };

  const typeClassName = type === "info" ? classes.typeInfo : classes.typeWarning;

  const containerClassName = React.useMemo(() => {
    const classNames = [classes.container];
    if (className) {
      classNames.push(className);
    }
    return classNames.join(" ");
  }, [className]);

  return (
    <Fade timeout={timeout} in={fadeing}>
      <div ref={containerRef} className={containerClassName}>
        <Paper className={[classes.paper, typeClassName].join(" ")}>
          <Grid wrap="nowrap" container justifyContent="space-between">
            <Grid className={classes.textContainer} item>
              <span className={classes.textContent}>{render ? render() : children}</span>
            </Grid>
            {onClose && (
              <Grid>
                <IconButton className={classes.closeIcon} onClick={handleClose}>
                  <IconRemove />
                </IconButton>
              </Grid>
            )}
          </Grid>
        </Paper>
      </div>
    </Fade>
  );
};

export default React.memo(NotificationMessage);
